/**
 * F28 (Wave 2C-ii) — admin email-suppressions list page.
 *
 * Route: `/email-suppressions`
 * Permission: `email_suppressions:read` (super_admin + support +
 *   content_moderator). Write actions are additionally gated client-side on
 *   `email_suppressions:write` (super_admin + support only) — the server
 *   re-enforces the matrix on every mutation.
 *
 * Layout mirrors F11 `/venue-claims` + F20 `/payment-disputes`:
 *   - Header (title + description)
 *   - Filter Chip row (source) + SearchInput
 *   - Table of suppressed addresses (most-recent first)
 *   - Cursor-based "Load more" pagination
 *   - "Add suppression" CTA (top-right) → AddSuppressionDialog
 *   - Per-row "Remove" action → RemoveSuppressionDialog
 *
 * Suppression rows are written automatically by Postmark bounce + complaint
 * webhooks (Decision D9). Manual admin entries are reserved for incident
 * response; the source label distinguishes them.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Chip,
  EmptyState,
  SearchInput,
  Skeleton,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type {
  AddEmailSuppressionInput,
  AdminPageMeta,
  EmailSuppression,
  EmailSuppressionSource,
  RemoveEmailSuppressionInput,
} from '@bravemove/types';
import {
  addEmailSuppression,
  listEmailSuppressions,
  removeEmailSuppression,
  type EmailSuppressionListRow,
  type ListEmailSuppressionsResult,
} from '../../api/emailSuppressions';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { AdminPagination } from '../../components/AdminPagination';
import { AddSuppressionDialog } from './AddSuppressionDialog';
import { RemoveSuppressionDialog } from './RemoveSuppressionDialog';

const PAGE_LIMIT = 25;

type FilterValue = 'all' | EmailSuppressionSource;

const FILTER_TABS: ReadonlyArray<{
  value: FilterValue;
  labelKey: string;
  defaultLabel: string;
}> = [
  { value: 'all', labelKey: 'admin:email_suppressions.filter_all', defaultLabel: 'All sources' },
  {
    value: 'hard_bounce',
    labelKey: 'admin:email_suppressions.filter_hard_bounce',
    defaultLabel: 'Hard bounces',
  },
  {
    value: 'soft_bounce_exhausted',
    labelKey: 'admin:email_suppressions.filter_soft_bounce_exhausted',
    defaultLabel: 'Soft bounces (exhausted)',
  },
  {
    value: 'spam_complaint',
    labelKey: 'admin:email_suppressions.filter_spam_complaint',
    defaultLabel: 'Spam complaints',
  },
  {
    value: 'admin_override',
    labelKey: 'admin:email_suppressions.filter_admin_override',
    defaultLabel: 'Manual admin',
  },
  {
    value: 'user_unsubscribe_required',
    labelKey: 'admin:email_suppressions.filter_user_unsubscribe_required',
    defaultLabel: 'User unsubscribe',
  },
];

function sourceLabelKey(source: EmailSuppressionSource): { key: string; fallback: string } {
  switch (source) {
    case 'hard_bounce':
      return { key: 'admin:email_suppressions.source.hard_bounce', fallback: 'Hard bounce' };
    case 'soft_bounce_exhausted':
      return {
        key: 'admin:email_suppressions.source.soft_bounce_exhausted',
        fallback: 'Soft bounce (exhausted)',
      };
    case 'spam_complaint':
      return { key: 'admin:email_suppressions.source.spam_complaint', fallback: 'Spam complaint' };
    case 'admin_override':
      return { key: 'admin:email_suppressions.source.admin_override', fallback: 'Admin override' };
    case 'user_unsubscribe_required':
      return {
        key: 'admin:email_suppressions.source.user_unsubscribe_required',
        fallback: 'User unsubscribe',
      };
    default:
      return { key: 'admin:email_suppressions.source.admin_override', fallback: source };
  }
}

function sourceBadgeTone(source: EmailSuppressionSource): 'danger' | 'warning' | 'neutral' {
  switch (source) {
    case 'hard_bounce':
    case 'spam_complaint':
      return 'danger';
    case 'soft_bounce_exhausted':
      return 'warning';
    case 'admin_override':
    case 'user_unsubscribe_required':
    default:
      return 'neutral';
  }
}

function formatRelative(iso: string, locale: string): string {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return iso;
  const diffMs = target - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const absMin = Math.abs(diffMin);
    if (absMin < 60) return rtf.format(diffMin, 'minute');
    const diffHour = Math.round(diffMin / 60);
    if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
    const diffDay = Math.round(diffHour / 24);
    return rtf.format(diffDay, 'day');
  } catch {
    return iso;
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}...` : s;
}

export function EmailSuppressionsPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const auth = useAuth();

  const canWrite = auth.adminUser?.permissions?.includes('email_suppressions:write') ?? false;

  const [filter, setFilter] = useState<FilterValue>('all');
  /** Raw input value — debounced into `q` (the fetch trigger) below. */
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<readonly EmailSuppressionListRow[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<EmailSuppression | null>(null);

  // NT-09 — debounce the search input ~300ms so each keystroke doesn't fire
  // an unbounced fetch. `q` (the fetch dependency) trails `qInput`. Setting
  // identical values on mount is a no-op (React bails on Object.is-equal
  // state), so this only resets the page when the user actually types.
  useEffect(() => {
    const handle = setTimeout(() => {
      setQ(qInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [qInput]);

  const buildFilter = useCallback(
    (targetPage: number) => ({
      ...(filter !== 'all' ? { source: filter } : {}),
      ...(q.trim() !== '' ? { q: q.trim() } : {}),
      page: targetPage,
      perPage: PAGE_LIMIT,
      limit: PAGE_LIMIT,
    }),
    [filter, q],
  );

  const fetchList = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const result: ListEmailSuppressionsResult = await listEmailSuppressions(
          buildFilter(targetPage),
        );
        setRows(result.data);
        setPagination(result.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [buildFilter, t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, q, page]);

  const onAddConfirm = async (input: AddEmailSuppressionInput) => {
    const result = await addEmailSuppression(input);
    // NT-07 — the server UPSERTs on duplicate; surface the honest variant
    // when the address was already on the list (reason/source refreshed).
    toast.push(
      result.created
        ? {
            tone: 'success',
            title: t('admin:email_suppressions.toast.added', {
              defaultValue: 'Address suppressed.',
            }),
          }
        : {
            tone: 'info',
            title: t('admin:email_suppressions.toast.added_updated', {
              defaultValue: 'Address was already suppressed — reason and notes updated.',
            }),
          },
    );
    setAddOpen(false);
    void fetchList(page);
  };

  const onRemoveConfirm = async (input: RemoveEmailSuppressionInput) => {
    if (!removeTarget) return;
    // Errors bubble up — the dialog's submit handler catches and renders the
    // generic-error Banner.
    const result = await removeEmailSuppression(removeTarget.emailAddress, input);
    // NT-09 — removed:false means another admin already removed the row;
    // don't pretend this action removed it.
    toast.push(
      result.removed
        ? {
            tone: 'success',
            title: t('admin:email_suppressions.toast.removed', {
              defaultValue: 'Address removed from suppression list.',
            }),
          }
        : {
            tone: 'info',
            title: t('admin:email_suppressions.toast.remove_already_removed', {
              defaultValue: 'Address was already removed (no longer on the suppression list).',
            }),
          },
    );
    setRemoveTarget(null);
    void fetchList(page);
  };

  const columns = useMemo<readonly TableColumn<EmailSuppressionListRow>[]>(
    () => [
      {
        key: 'email',
        header: t('admin:email_suppressions.table.column_email', { defaultValue: 'Email' }),
        cell: (row) => (
          <span
            className="font-mono text-[12.5px] text-ink"
            data-testid={`email-suppression-email-${row.emailAddress}`}
          >
            {row.emailAddress}
          </span>
        ),
      },
      {
        key: 'source',
        header: t('admin:email_suppressions.table.column_source', { defaultValue: 'Source' }),
        cell: (row) => {
          const lbl = sourceLabelKey(row.source);
          return (
            <Badge
              tone={sourceBadgeTone(row.source)}
              size="sm"
              data-testid={`email-suppression-source-${row.emailAddress}`}
            >
              {t(lbl.key, { defaultValue: lbl.fallback })}
            </Badge>
          );
        },
      },
      {
        key: 'reason',
        header: t('admin:email_suppressions.table.column_reason', { defaultValue: 'Reason' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2 leading-snug">{truncate(row.reason, 80)}</span>
        ),
      },
      {
        key: 'suppressed_at',
        header: t('admin:email_suppressions.table.column_suppressed_at', {
          defaultValue: 'Suppressed',
        }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatRelative(row.suppressedAt, locale)}</span>
        ),
      },
      {
        key: 'added_by',
        header: t('admin:email_suppressions.table.column_added_by', { defaultValue: 'Added by' }),
        // NT-09 — prefer the server-resolved admin display name; fall back to
        // the raw UUID (display-name-less admin), then 'System' (webhook rows).
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {row.addedByAdminDisplayName ??
              row.addedByAdminId ??
              t('admin:email_suppressions.table.row_added_by_system', {
                defaultValue: 'System',
              })}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('admin:email_suppressions.table.column_actions', { defaultValue: 'Actions' }),
        align: 'end',
        cell: (row) =>
          canWrite ? (
            <div className="flex justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setRemoveTarget(row)}
                data-testid={`email-suppression-remove-${row.emailAddress}`}
              >
                {t('admin:email_suppressions.table.row_remove', { defaultValue: 'Remove' })}
              </Button>
            </div>
          ) : (
            <span data-testid={`email-suppression-readonly-${row.emailAddress}`}>—</span>
          ),
      },
    ],
    [t, locale, canWrite],
  );

  return (
    <div className="space-y-6" data-testid="email-suppressions-page">
      <PageHeader
        title={t('admin:email_suppressions.page_title', { defaultValue: 'Email suppressions' })}
        subtitle={t('admin:email_suppressions.page_description', {
          defaultValue: 'Addresses BraveMove will not send email to until manually removed.',
        })}
        actions={
          canWrite ? (
            <Button
              variant="primary"
              onClick={() => setAddOpen(true)}
              data-testid="email-suppressions-add-button"
            >
              {t('admin:email_suppressions.add_button', { defaultValue: 'Add suppression' })}
            </Button>
          ) : null
        }
      />

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div
          role="tablist"
          aria-label={t('admin:email_suppressions.filter_label', {
            defaultValue: 'Filter by source',
          })}
          className="flex flex-wrap items-center gap-2"
        >
          {FILTER_TABS.map((tab) => (
            <Chip
              key={tab.value}
              selected={filter === tab.value}
              onClick={() => {
                setFilter(tab.value);
                setPage(1);
              }}
              data-testid={`email-suppressions-filter-${tab.value}`}
            >
              {t(tab.labelKey, { defaultValue: tab.defaultLabel })}
            </Chip>
          ))}
        </div>
        <div className="max-w-sm w-full md:w-72">
          <SearchInput
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder={t('admin:email_suppressions.search_placeholder', {
              defaultValue: 'Search by email address',
            })}
            data-testid="email-suppressions-search"
          />
        </div>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="email-suppressions-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="flag" size={36} color="currentColor" />}
          title={t('admin:email_suppressions.empty.title', {
            defaultValue: 'No email addresses are suppressed',
          })}
          description={t('admin:email_suppressions.empty.description', {
            defaultValue:
              'Bounces and complaints will appear here automatically. Manual entries are reserved for incident response.',
          })}
        />
      ) : (
        <Table
          data={rows as EmailSuppressionListRow[]}
          columns={columns as TableColumn<EmailSuppressionListRow>[]}
          rowKey={(row) => row.emailAddress}
        />
      )}

      {pagination && !loading && rows.length > 0 ? (
        <AdminPagination
          pagination={pagination}
          shownCount={rows.length}
          onChange={setPage}
          testId="email-suppressions-pagination"
        />
      ) : null}

      {canWrite ? (
        <AddSuppressionDialog
          open={addOpen}
          onConfirm={onAddConfirm}
          onClose={() => setAddOpen(false)}
        />
      ) : null}

      {canWrite && removeTarget ? (
        <RemoveSuppressionDialog
          open={removeTarget !== null}
          emailAddress={removeTarget.emailAddress}
          onConfirm={onRemoveConfirm}
          onClose={() => setRemoveTarget(null)}
        />
      ) : null}
    </div>
  );
}

export default EmailSuppressionsPage;
